import { useRef } from "react";
import { useApolloClient } from "@apollo/client/react";
import type { ApolloClient } from "@apollo/client";

import type { CACHE_AND_NETWORK_THEN_CACHE_FIRST } from "./fetchPolicies";

type FetchPolicyConfig = typeof CACHE_AND_NETWORK_THEN_CACHE_FIRST;

const CACHE_AND_NETWORK: FetchPolicyConfig = {
  fetchPolicy: "cache-and-network",
  nextFetchPolicy: "cache-first",
};
const CACHE_FIRST: FetchPolicyConfig = { fetchPolicy: "cache-first" };

/** Calls within this window of a network refresh share it. */
export const DEFAULT_FRESH_MS = 30_000;

const lastRefresh = new WeakMap<ApolloClient, Map<string, number>>();

/**
 * Hooks that call useQuery with cache-and-network each hit the network on
 * mount, so several components using the same hook fire the same request more
 * than once during startup. The first caller per client and key refreshes from
 * the network; callers within `freshMs` of that read the cache it fills, and a
 * later mount refreshes again.
 *
 * Pass `enabled: false` while the query is skipped so a skipped call does not
 * use up the refresh. A caller keeps the policy it first got for a client, so it
 * does not flip to cache-first on its next render, and decides again if the
 * client changes.
 */
export const useCacheAndNetworkOnce = (
  key: string,
  { enabled = true, freshMs = DEFAULT_FRESH_MS } = {}
): FetchPolicyConfig => {
  const client = useApolloClient();
  const decided = useRef<{
    client: ApolloClient;
    policy: FetchPolicyConfig;
  } | null>(null);

  if (!enabled) {
    return CACHE_FIRST;
  }

  // Decide again when the client changes (a workspace switch), since the new
  // client has not refreshed yet and the cache may hold the old workspace.
  if (decided.current == null || decided.current.client !== client) {
    let refreshedAt = lastRefresh.get(client);
    if (!refreshedAt) {
      refreshedAt = new Map();
      lastRefresh.set(client, refreshedAt);
    }
    const last = refreshedAt.get(key);
    const now = Date.now();
    if (last != null && now - last < freshMs) {
      decided.current = { client, policy: CACHE_FIRST };
    } else {
      refreshedAt.set(key, now);
      decided.current = { client, policy: CACHE_AND_NETWORK };
    }
  }

  return decided.current.policy;
};
